import type { StatusTone } from '@vnetwork/ui/data';
import type { TicketStatus, TicketPriority, TicketTypeOption } from '@/api/support';

/**
 * Short English labels for the create form, keyed by ticket_type_key. The
 * DataCentral `ticket_type_title` is verbose Vietnamese; the partner console is
 * English-only, so we display these instead (the submitted value is still the
 * real ticket_type_id). Unknown keys fall back to the API title.
 */
export const TICKET_TYPE_LABEL: Record<string, string> = {
  error_service_incident: 'Service incident',
  error_service_problem: 'Service problem',
  support_requests_check_or_change: 'Config / feature change',
  support_requests_fg_pwd: 'Account / password help',
  support_requests_training: 'Usage guidance',
  support_requests_others: 'Other request',
};

export function ticketTypeLabel(t: TicketTypeOption): string {
  return TICKET_TYPE_LABEL[t.ticket_type_key] ?? t.ticket_type_title;
}

/**
 * English label for a ticket type from its key — use this instead of the
 * Vietnamese `ticket_type_title`. Falls back to a humanized key for unmapped types.
 */
export function ticketTypeLabelFromKey(key: string, fallback = 'Support request'): string {
  if (TICKET_TYPE_LABEL[key]) return TICKET_TYPE_LABEL[key];
  if (!key) return fallback;
  return key
    .replace(/^support_requests?_/, '')
    .replace(/^error_service_/, '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** StatusPill tone + label for a ticket status. `loading` shows a spinner. */
export const TICKET_STATUS_TONE: Record<TicketStatus, StatusTone> = {
  opening: 'info',
  'work-in-progress': 'warning',
  pending: 'muted',
  completed: 'success',
  closed: 'neutral',
  cancelled: 'danger',
};

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  opening: 'Opening',
  'work-in-progress': 'In progress',
  pending: 'Pending',
  completed: 'Completed',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/** Statuses where the conversation is over — the reply composer is disabled. */
export const TERMINAL_STATUSES: TicketStatus[] = ['completed', 'closed', 'cancelled'];

type BadgeTone = 'neutral' | 'primary' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export const TICKET_PRIORITY_TONE: Record<TicketPriority, BadgeTone> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

export const TICKET_PRIORITY_LABEL: Record<TicketPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

// Date formatters are centralized in @/lib/format-date (which also rejects
// backend zero-dates). Re-exported so existing './shared' import sites work.
export { formatDate, formatDateTime } from '@/lib/format-date';
